'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Section } from '@/components/ui/Section';
import styles from './WebshopShowcase.module.css';

export interface WebshopShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    jumbo?: string;
    lindenhof?: string;
    annemax?: string;
    deberen?: string;
  };
  mobileAltTexts?: {
    jumbo?: string;
    lindenhof?: string;
    annemax?: string;
    deberen?: string;
  };
}

// Webshop Mockup configuration with brand-specific glow colors
// Professional layout: MacBook + iPhone combination (Apple-style)
const WEBSHOP_MOCKUP_CONFIG = [
  {
    id: 'jumbo',
    macbook: '/images/webshop-mockups/webshop-jumbo-online-ordering.webp',
    mobile: '/images/webshop-mockups/webshop-jumbo-online-ordering-mobile.webp',
    color: '#fcc300', // Jumbo brand yellow
  },
  {
    id: 'lindenhof',
    macbook: '/images/webshop-mockups/webshop-grandcafe-lindenhof-takeaway.webp',
    mobile: '/images/webshop-mockups/webshop-grandcafe-lindenhof-takeaway-mobile.webp',
    color: '#ebe9da', // Grand Café de Lindenhof brand beige
  },
  {
    id: 'annemax',
    macbook: '/images/webshop-mockups/webshop-anne-max-delivery.webp',
    mobile: '/images/webshop-mockups/webshop-anne-max-delivery-mobile.webp',
    color: '#5e9ea1', // Anne&Max brand teal
  },
  {
    id: 'deberen',
    macbook: '/images/webshop-mockups/webshop-de-beren-ordering.webp',
    mobile: '/images/webshop-mockups/webshop-de-beren-ordering-mobile.webp',
    color: '#1d3b42', // De Beren brand dark blue
  },
] as const;

/**
 * WebshopShowcase Component
 * Displays 4 webshop ordering mockups with floating animation effect
 * On mobile: auto-playing slider with touch support
 * Uses macbook mockups instead of phone mockups
 */
export function WebshopShowcase({ title, subtitle, altTexts, mobileAltTexts }: WebshopShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Detect mobile and desktop for smart image loading
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsDesktop(width > 768);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Auto-play slider on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // ✅ PERFORMANCE FIX: SSR-friendly visibility with animations
  // Content is always visible, animations trigger on mount for smooth experience
  useEffect(() => {
    // Make content visible immediately on mount (SSR-friendly)
    const webshopMockups = sectionRef.current?.querySelectorAll(`.${styles.webshop}`);
    webshopMockups?.forEach((mockup) => {
      mockup.classList.add(styles.visible);
    });
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 75) {
      // Swipe left
      setCurrentSlide((prev) => (prev + 1) % 4);
    }
    if (touchStartX.current - touchEndX.current < -75) {
      // Swipe right
      setCurrentSlide((prev) => (prev - 1 + 4) % 4);
    }
  };

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
        <div className={styles.background}>
          <div className={styles.gradientOrb1} />
          <div className={styles.gradientOrb2} />
          <div className={styles.grid} />
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>

          <div 
            className={styles.webshopsGrid}
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={isMobile ? {
              transform: `translateX(-${currentSlide * 100}%)`,
              transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            } : {}}
          >
            {WEBSHOP_MOCKUP_CONFIG.map((mockup, index) => (
              <div
                key={mockup.id}
                className={styles.webshop}
                style={{
                  animationDelay: `${index * 0.15}s`,
                }}
              >
                <div className={styles.webshopInner}>
                  <div
                    className={styles.webshopGlow}
                    style={{
                      background: `radial-gradient(circle at center, ${mockup.color}40 0%, transparent 70%)`,
                    }}
                  />
                  <div className={styles.deviceWrapper}>
                    {/* MacBook - Background */}
                    <div className={styles.macbookWrapper}>
                      <Image
                        src={mockup.macbook}
                        alt={altTexts?.[mockup.id as keyof typeof altTexts] || `Jamezz webshop - ${mockup.id}`}
                        width={900}
                        height={600}
                        className={styles.macbookImage}
                        loading={
                          isDesktop 
                            ? (index < 2 ? "eager" : "lazy")  // Desktop: eerste 2 eager
                            : (index === 0 ? "eager" : "lazy")  // Mobile: alleen eerste eager
                        }
                        priority={index === 0}
                        quality={85}
                      />
                    </div>
                    
                    {/* iPhone - Foreground (positioned in front/beside) */}
                    <div className={styles.mobileWrapper}>
                      <Image
                        src={mockup.mobile}
                        alt={mobileAltTexts?.[mockup.id as keyof typeof mobileAltTexts] || `Jamezz mobile webshop - ${mockup.id}`}
                        width={300}
                        height={600}
                        className={styles.mobileImage}
                        loading={
                          isDesktop 
                            ? (index < 2 ? "eager" : "lazy")  // Desktop: eerste 2 eager
                            : (index === 0 ? "eager" : "lazy")  // Mobile: alleen eerste eager
                        }
                        priority={index === 0}
                        quality={85}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation dots - CSS hides on desktop, visible immediately on mobile */}
            <div className={styles.sliderDots}>
              {WEBSHOP_MOCKUP_CONFIG.map((mockup, index) => (
                <button
                  key={mockup.id}
                  className={`${styles.dot} ${currentSlide === index ? styles.active : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  style={currentSlide === index ? {
                    backgroundColor: mockup.color,
                  } : {}}
                />
              ))}
            </div>
        </div>
      </div>
    </Section>
  );
}

